import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Download, ShieldCheck } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1488751045188-3c55bbf9a3fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nJTIwbGFwdG9wJTIwd29ya3NwYWNlfGVufDB8fHx8MTc5MDczOTUzM3ww&ixlib=rb-4.1.0&q=85";

export function HeroVisual() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mx.set((event.clientX - rect.left) / rect.width - 0.5);
    my.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <motion.div
      data-testid="hero-visual"
      onMouseMove={handleMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ opacity: 0, scale: 0.94, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className="relative mx-auto aspect-[4/5] w-full max-w-[520px] perspective-1000"
    >
      <div className="absolute -left-8 top-10 h-40 w-40 rounded-full bg-amber-500/20 blur-3xl" />
      <div className="absolute -bottom-8 -right-8 h-48 w-48 rounded-full bg-[#3F5E4D]/20 blur-3xl" />
      <div className="spotlight-frame relative h-full overflow-hidden rounded-[2.5rem] border border-white/70 bg-white shadow-[0_35px_100px_rgba(17,19,21,0.18)]" style={{ transform: "translateZ(30px)" }}>
        <img src={HERO_IMAGE} alt="Small-business workspace with a laptop and planning materials" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111315]/72 via-transparent to-white/10" />
        <div className="absolute bottom-6 left-6 right-6 rounded-3xl border border-white/20 bg-white/14 p-5 text-white backdrop-blur-xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-300">Built for daily momentum</p>
          <p className="mt-2 font-heading text-2xl font-bold tracking-tight">Less admin. Clearer next steps.</p>
        </div>
      </div>
      <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="absolute -left-5 top-16 rounded-3xl border border-white/70 bg-white/90 p-4 shadow-xl backdrop-blur" style={{ transform: "translateZ(70px)" }}>
        <ShieldCheck className="h-5 w-5 text-[#3F5E4D]" />
        <p className="mt-2 text-xs font-bold">Secure checkout ready</p>
      </motion.div>
      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="absolute -right-4 bottom-24 rounded-3xl border border-white/70 bg-[#111315] p-4 text-white shadow-xl" style={{ transform: "translateZ(80px)" }}>
        <Download className="h-5 w-5 text-amber-400" />
        <p className="mt-2 text-xs font-bold">Delivery after payment</p>
      </motion.div>
      <div className="absolute -bottom-6 left-10 rounded-full bg-[#D97706] p-4 text-white shadow-2xl">
        <ArrowDownRight className="h-6 w-6" />
      </div>
    </motion.div>
  );
}
